import useSwrHook from '@components/_hooks/useSwrHook';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import ErrorContainer from '../Reusables/ErrorContainer';
import useAlert from '@components/_hooks/useAlert';
import AlertComponent from '../Reusables/AlertComponent';
import Box from '@mui/material/Box';

const PageDataContext = createContext();

export const PageDataProvider = ({
    children,
    uri,
    params = {},
    type = 'base',
    swrOptions = {},
    initialFunctions = {},
    initialPageData = {},
    pageComponents = [],
    skeleton = null,
    REFRESH_EVERY = null,
    noDataError = false,
    noDataCondition = null,
    showFailedAlert = false,
    showFailedBlock = false,
    errorMessage = "No results were found...",
    allow = true,
    revalidateLoader = false,

    // wrapper / layout
    title = null,
    renderWrapper = null,
    renderFailedBlock = true,
}) => {
    const { topMiddleAlert } = useAlert();

    const mergedSwrOptions = useMemo(() => ({
        ...swrOptions,
        ...(REFRESH_EVERY
            ? {
                refreshInterval: REFRESH_EVERY * 1000,
                refreshWhenHidden: false,
                refreshWhenOffline: false,
                errorRetryCount: 3,
            }
            : {}),
    }), [swrOptions, REFRESH_EVERY]);

    const { data, status, message, error, isLoading, isValidating, mutate } = useSwrHook(
        uri,
        { params },
        type,
        mergedSwrOptions,
        allow
    );

    /**
     * Keep these states so existing components using setPageData / setFunctions still work.
     * But do NOT mirror initialPageData / initialFunctions into them with useEffect.
     * That was the likely render loop.
     */
    const [pageData, setPageData] = useState({});
    const [functions, setFunctions] = useState({});

    useEffect(() => {
        if ((error || status === 'failed') && showFailedAlert && !isValidating) {
            const alertMessage =
                (error ? (error?.response?.data?.message || error?.message) : null) ||
                (status === 'failed' ? message : null) ||
                'Something went wrong';

            topMiddleAlert('ERROR', alertMessage);
        }
    }, [error, status, showFailedAlert, message, isValidating, topMiddleAlert]);

    /**
     * Merge order:
     * 1. initialPageData from wrapper/hooks
     * 2. local pageData updates from children
     * 3. remote SWR object data
     */
    const activePageData = useMemo(() => {
        const remoteData =
            data &&
                typeof data === 'object' &&
                !Array.isArray(data)
                ? data
                : {};

        return {
            ...initialPageData,
            ...pageData,
            ...remoteData,
        };
    }, [initialPageData, pageData, data]);

    /**
     * Keep same name.
     * No need to merge initialPageData again here.
     */
    const pageState = activePageData;

    /**
     * Merge initial functions with any functions set later by children.
     */
    const activeFunctions = useMemo(() => ({
        ...initialFunctions,
        ...functions,
    }), [initialFunctions, functions]);

    const showNoDataError =
        noDataError &&
        data?.[noDataCondition]?.length === 0;

    const showFailedDataBlock =
        showFailedBlock &&
        error?.message;

    const showSkeleton =
        skeleton != null &&
        (
            (revalidateLoader && isValidating) ||
            ((isLoading || isValidating) && Object.keys(activePageData).length === 0)
        );

    const FailedComponent = showFailedDataBlock ? (
        <AlertComponent
            title={error?.response?.data?.message || error.message}
            severity="error"
        />
    ) : null;

    const content = useMemo(() => {
        if (showFailedDataBlock && renderFailedBlock) {
            return FailedComponent;
        }

        if (showSkeleton) {
            return skeleton;
        }

        if (showNoDataError) {
            return <ErrorContainer error={errorMessage} />;
        }

        return children;
    }, [
        showFailedDataBlock,
        renderFailedBlock,
        FailedComponent,
        showSkeleton,
        skeleton,
        showNoDataError,
        errorMessage,
        children,
    ]);

    const wrapperProps = useMemo(() => ({
        title,
        content,
        data,
        status,
        message,
        error,
        isLoading,
        isValidating,
        showSkeleton,
        showFailedDataBlock,
        showNoDataError,
        FailedComponent,
    }), [
        title,
        content,
        data,
        status,
        message,
        error,
        isLoading,
        isValidating,
        showSkeleton,
        showFailedDataBlock,
        showNoDataError,
        FailedComponent,
    ]);

    const contextValue = useMemo(() => ({
        data,
        status,
        message,
        error,
        isLoading,
        isValidating,
        mutate,

        // same external names
        functions: activeFunctions,
        pageData: activePageData,
        pageState,
        pageComponents,
        setPageData,
        setFunctions,
        FailedComponent,
    }), [
        data,
        status,
        message,
        error,
        isLoading,
        isValidating,
        mutate,
        activeFunctions,
        activePageData,
        pageState,
        pageComponents,
        FailedComponent,
    ]);

    return (
        <PageDataContext.Provider value={contextValue}>
            {renderWrapper ? (
                renderWrapper(wrapperProps)
            ) : (
                <Box>
                    {content}
                </Box>
            )}
        </PageDataContext.Provider>
    );
};

export const usePageData = () => useContext(PageDataContext);